import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { tr } from "@/lib/i18n";
const FIELDS = [
  ["legal_name", tr("Ragione sociale"), "Sollar AG", 2], ["address", tr("Indirizzo"), tr("Bahnhofstrasse 1"), 2],
  ["zip", tr("CAP"), "6003", 1], ["city", tr("Città"), "Luzern", 1], ["country", tr("Paese"), tr("Svizzera"), 2],
  ["phone", tr("Telefono"), "+41 41 000 00 00", 1], ["email", tr("Email"), "info@azienda.ch", 1],
  ["website", tr("Sito web"), "www.azienda.ch", 1], ["vat", tr("Partita IVA / IDI"), "CHE-123.456.789", 1],
];

const LogoBox = ({ admin, hasLogo, onChange }) => {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!hasLogo) { setSrc(null); return undefined; }
    let url;
    api.get("/company/logo", { responseType: "blob" }).then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [hasLogo, busy]);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error(tr("Il logo deve essere al massimo di 2 MB"));
    setBusy(true);
    try { await api.put("/company/logo", file, { headers: { "Content-Type": file.type } }); toast.success(tr("Logo caricato")); onChange(true); }
    catch (ex) { toast.error(apiError(ex)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const remove = async () => { try { await api.delete("/company/logo"); toast.success(tr("Logo rimosso")); onChange(false); } catch (ex) { toast.error(apiError(ex)); } };
  return (
    <div className="space-y-3">
      <div className="h-32 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center p-3" data-testid="company-logo-box">
        {src ? <img src={src} alt={tr("Logo aziendale")} className="max-h-full max-w-full object-contain" data-testid="company-logo-img" />
          : <span className="text-xs text-slate-400 text-center">{tr("Nessun logo")}<br />{tr("PNG, JPG o WEBP, max 2 MB")}</span>}
      </div>
      {admin && (
        <div className="flex gap-2">
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="company-logo-input" />
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current.click()} data-testid="company-logo-upload-btn"><ImagePlus className="w-4 h-4 mr-1.5" />{busy ? tr("Caricamento…") : hasLogo ? tr("Sostituisci") : tr("Carica logo")}</Button>
          {hasLogo && <Button type="button" variant="ghost" size="sm" onClick={remove} className="hover:text-amber-700" data-testid="company-logo-remove-btn"><Trash2 className="w-4 h-4 mr-1" />{tr("Rimuovi")}</Button>}
        </div>
      )}
    </div>
  );
};

export const CompanyProfileCard = ({ c, reload }) => {
  const [f, setF] = useState(c.profile);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/company/profile", f); toast.success(tr("Dati aziendali salvati")); reload(); }
    catch (ex) { toast.error(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-lg sr-rise" style={{ animationDelay: "40ms" }} data-testid="company-profile-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2"><FileText className="w-4 h-4 text-[#0F52BA]" />
        <div><h2 className="font-display font-semibold">{tr("Logo e dati aziendali")}</h2>
          <p className="text-sm text-slate-500">{tr("Compaiono nell'intestazione e nel piè di pagina di ogni report PDF.")}{!c.is_admin && tr(" Solo l'admin azienda può modificarli.")}</p></div></div>
      <form onSubmit={save} className="p-5 grid md:grid-cols-[220px_1fr] gap-6">
        <LogoBox admin={c.is_admin} hasLogo={c.has_logo} onChange={() => reload()} />
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {FIELDS.map(([k, label, ph, span]) => (
            <div key={k} className={span === 2 ? "col-span-2" : ""}>
              <Label className="text-xs font-tech uppercase tracking-wider text-slate-500">{label}</Label>
              <Input value={f[k] || ""} readOnly={!c.is_admin} placeholder={c.is_admin ? ph : ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`h-9 mt-1 ${c.is_admin ? "" : "bg-slate-50 text-slate-800 focus-visible:ring-0"}`} data-testid={`company-profile-${k}`} />
            </div>
          ))}
          {c.is_admin && <div className="col-span-2 flex justify-end"><Button type="submit" disabled={busy} className="bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid="company-profile-save-btn"><Save className="w-4 h-4 mr-1.5" />{tr("Salva dati")}</Button></div>}
        </div>
      </form>
    </section>
  );
};
